import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import DogCard from "../components/DogCard";
import RobotSnacksBanner from "../components/RobotSnacksBanner";
import { AlertTriangle, ArrowRight, Heart, Search, Sparkles, Users, Globe, Bell, ShieldCheck, Gift, Rocket, KeyRound } from "lucide-react";
import SnacksFundTracker from "../components/SnacksFundTracker";
import { Button } from "../components/ui/button";
import { useI18n } from "../lib/i18n";

export default function Home() {
    const { t } = useI18n();
    const [stats, setStats] = useState(null);
    const [atRisk, setAtRisk] = useState([]);
    const [donations, setDonations] = useState(null);
    const [regions, setRegions] = useState([]);

    useEffect(() => {
        api.get("/stats/overview").then((r) => setStats(r.data));
        api.get("/dogs/at-risk").then((r) => setAtRisk(r.data.slice(0, 4)));
        api.get("/donations/stats").then((r) => setDonations(r.data));
        api.get("/stats/regions").then((r) => setRegions(r.data));
    }, []);

    return (
        <div data-testid="home-page">
            {/* Urgent Bar */}
            <div className="bg-[var(--rl-urgent)] text-white text-sm py-2 overflow-hidden relative" data-testid="urgent-ticker">
                <div className="flex whitespace-nowrap ticker-track">
                    {[...Array(2)].map((_, i) => (
                        <div key={i} className="flex items-center gap-8 px-4">
                            {atRisk.map((d) => (
                                <Link key={`${i}-${d.id}`} to={`/dogs/${d.id}`} className="flex items-center gap-2 hover:underline">
                                    <AlertTriangle className="w-3.5 h-3.5" />
                                    <span className="font-semibold">{d.name}</span>
                                    <span className="opacity-80">·</span>
                                    <span className="opacity-90">{d.location}</span>
                                </Link>
                            ))}
                        </div>
                    ))}
                </div>
            </div>

            {/* Hero */}
            <section className="relative overflow-hidden grain" data-testid="hero">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-10 items-start">
                    <div className="lg:col-span-7 fade-up">
                        <div className="eyebrow mb-6">{t("hero.eyebrow")}</div>
                        <h1 className="font-serif text-[44px] leading-[1.02] sm:text-6xl lg:text-[76px] tracking-tight">
                            {t("hero.line1")}<br />
                            <em className="not-italic text-[var(--rl-primary)]">{t("hero.line2")}</em>{" "}
                            {t("hero.line3")}
                        </h1>
                        <p className="mt-6 text-lg text-[var(--rl-text-2)] max-w-xl leading-relaxed">
                            {t("hero.body")}
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/at-risk">
                                <Button className="btn-urgent rounded-full h-12 px-6 text-sm font-semibold" data-testid="hero-atrisk-btn">
                                    <AlertTriangle className="w-4 h-4 mr-2" /> {t("hero.cta1")}
                                </Button>
                            </Link>
                            <Link to="/matchmaking">
                                <Button variant="outline" className="rounded-full h-12 px-6 text-sm font-semibold border-[var(--rl-border-strong)]" data-testid="hero-match-btn">
                                    <Sparkles className="w-4 h-4 mr-2" /> {t("hero.cta2")}
                                </Button>
                            </Link>
                            <Link to="/alerts">
                                <Button variant="outline" className="rounded-full h-12 px-6 text-sm font-semibold border-[var(--rl-border-strong)]" data-testid="hero-alerts-btn">
                                    <Bell className="w-4 h-4 mr-2" /> Alert me
                                </Button>
                            </Link>
                        </div>
                        {stats && (
                            <div className="mt-10 grid grid-cols-4 max-w-lg gap-6 text-left" data-testid="hero-stats">
                                <Stat label={t("stat.atRiskNow")} value={stats.at_risk} accent />
                                <Stat label={t("stat.dogsListed")} value={stats.dogs_total} />
                                <Stat label={t("stat.countries")} value={stats.countries} />
                                <Stat label={t("stat.rescuePartners")} value={stats.rescues + stats.shelters} />
                            </div>
                        )}
                    </div>
                    <div className="lg:col-span-5 relative fade-up">
                        <div className="relative">
                            <div className="absolute -top-6 -left-6 w-24 h-24 rounded-full bg-[var(--rl-sage)] -z-10"></div>
                            <div className="absolute -bottom-6 -right-4 w-28 h-28 rounded-full bg-[#f5d8ce] -z-10"></div>
                            <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-xl border border-[var(--rl-border)]">
                                <img
                                    src="https://images.unsplash.com/photo-1649923625793-af1cbd33c8bd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxyZXNjdWUlMjBkb2clMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODU0NjI5MDR8MA&ixlib=rb-4.1.0&q=85"
                                    alt="Soulful rescue dog waiting for a home"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="absolute -bottom-5 left-5 right-5 bg-white/95 backdrop-blur-md border border-[var(--rl-border)] rounded-xl p-4 shadow-lg">
                                <div className="eyebrow text-[var(--rl-urgent)]">Featured · At Risk</div>
                                <div className="font-serif text-xl mt-1">Meet Biscuit</div>
                                <div className="text-xs text-[var(--rl-text-2)]">4y · Pit Bull Mix · Houston, TX</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Two-Doors band — 3-second clarity: what visitors can access + what rescues need */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 lg:py-12" data-testid="two-doors-band">
                <div className="grid md:grid-cols-2 gap-5">
                    {/* Door 1 — For Rescues */}
                    <Link to="/resources" data-testid="door-resources" className="group relative overflow-hidden rounded-2xl border border-[var(--rl-border)] bg-[var(--rl-cream)] p-8 lg:p-10 transition-all hover:-translate-y-0.5 hover:border-[var(--rl-primary)]">
                        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-[var(--rl-sage)]/50 group-hover:scale-110 transition-transform" />
                        <div className="relative">
                            <div className="w-12 h-12 rounded-full bg-[var(--rl-text)] text-white flex items-center justify-center mb-5">
                                <ShieldCheck className="w-6 h-6" />
                            </div>
                            <div className="eyebrow text-[var(--rl-text-2)] mb-2">For Rescues &amp; Shelters</div>
                            <h2 className="font-serif text-3xl lg:text-4xl leading-[1.05]">
                                See every resource<br />available to <em className="not-italic text-[var(--rl-primary)]">your rescue.</em>
                            </h2>
                            <p className="mt-4 text-sm text-[var(--rl-text-2)] max-w-md leading-relaxed">
                                Transport routes, vetted foster homes, volunteer pools, vet partnerships, corporate donors, translation, and legal escort — all in one working toolkit.
                            </p>
                            <div className="mt-5 flex flex-wrap gap-2 text-xs">
                                {["Transport", "Fosters", "Volunteers", "Vet partners", "Corporate donors", "AI tools"].map((chip) => (
                                    <span key={chip} className="px-3 py-1 bg-white border border-[var(--rl-border)] rounded-full text-[var(--rl-text-2)]">{chip}</span>
                                ))}
                            </div>
                            <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--rl-primary)] group-hover:gap-2.5 transition-all">
                                Open the toolkit <ArrowRight className="w-4 h-4" />
                            </span>
                        </div>
                    </Link>

                    {/* Door 2 — Ways to Give */}
                    <Link to="/ways-to-give" data-testid="door-ways-to-give" className="group relative overflow-hidden rounded-2xl bg-[var(--rl-primary)] text-white p-8 lg:p-10 transition-all hover:-translate-y-0.5">
                        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10 group-hover:scale-110 transition-transform" />
                        <div className="relative">
                            <div className="w-12 h-12 rounded-full bg-white/15 text-white flex items-center justify-center mb-5">
                                <Gift className="w-6 h-6" />
                            </div>
                            <div className="eyebrow text-white/80 mb-2">Ways to Give</div>
                            <h2 className="font-serif text-3xl lg:text-4xl leading-[1.05]">
                                Nine ways to help.<br />
                                <em className="not-italic">Pick yours in 30 seconds.</em>
                            </h2>
                            <p className="mt-4 text-sm text-white/85 max-w-md leading-relaxed">
                                Money, monthly recurring, supplies, fostering, transport driving, stock &amp; DAF gifts, volunteering, alert boosting, and Robot Snacks — every dollar and hour routes directly to a rescue.
                            </p>
                            <div className="mt-5 flex flex-wrap gap-2 text-xs">
                                {["One-time", "Monthly", "Supplies", "Foster", "Drive", "Stock/DAF/Crypto"].map((chip) => (
                                    <span key={chip} className="px-3 py-1 bg-white/15 border border-white/25 rounded-full text-white">{chip}</span>
                                ))}
                            </div>
                            <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold group-hover:gap-2.5 transition-all">
                                See all nine ways <ArrowRight className="w-4 h-4" />
                            </span>
                        </div>
                    </Link>
                </div>
            </section>

            {/* Snacks Fund Tracker — always-visible running balance */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-6" data-testid="home-snacks-fund-section">
                <SnacksFundTracker />
            </section>

            {/* Register Your Rescue band — big, bold entry point */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-2" data-testid="register-rescue-band">
                <div className="group rounded-2xl bg-[#1f1b16] text-[#f5e9d7] p-6 lg:p-8 relative overflow-hidden">
                    <div className="absolute -right-4 -top-4 w-40 h-40 rounded-full bg-[#f2b787]/15" />
                    <div className="relative grid lg:grid-cols-[auto_1fr_auto] gap-5 items-center">
                        <div className="w-12 h-12 rounded-full bg-[#f2b787] text-[#1f1b16] flex items-center justify-center shrink-0">
                            <Rocket className="w-6 h-6" />
                        </div>
                        <div>
                            <div className="eyebrow text-[#f2b787] mb-1 inline-flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5" /> Are you a rescue?</div>
                            <div className="font-serif text-2xl lg:text-3xl leading-tight">
                                Register in 2 minutes.<br />
                                <em className="not-italic text-[#f2b787]">Get an API key, portal, and network reach.</em>
                            </div>
                            <p className="text-sm opacity-80 mt-2 max-w-xl">
                                List your organization, post current needs, publish resources you can offer, and sync your intake system via our REST API.
                            </p>
                        </div>
                        <div className="flex flex-col gap-2 min-w-[190px]">
                            <Link to="/join" data-testid="home-join-btn"><Button className="w-full rounded-full h-11 bg-[#f2b787] text-[#1f1b16] hover:bg-[#e6a670]"><Rocket className="w-4 h-4 mr-2" /> Register your rescue</Button></Link>
                            <Link to="/developers" data-testid="home-api-docs-btn"><Button variant="outline" className="w-full rounded-full h-11 border-white/25 text-[#f5e9d7] hover:bg-white/10 hover:text-white"><KeyRound className="w-4 h-4 mr-2" /> API docs</Button></Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* At Risk Preview */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16" data-testid="atrisk-section">
                <div className="flex items-end justify-between mb-8">
                    <div>
                        <div className="eyebrow text-[var(--rl-urgent)] mb-3">Time-critical</div>
                        <h2 className="font-serif text-3xl sm:text-5xl tracking-tight">At risk right now</h2>
                        <p className="mt-2 text-[var(--rl-text-2)] max-w-xl">These dogs need a pledge, foster, or transport commitment before their scheduled date.</p>
                    </div>
                    <Link to="/at-risk" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-[var(--rl-primary)] font-semibold hover:underline">
                        View all at-risk <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {atRisk.map((d) => <DogCard key={d.id} dog={d} urgent />)}
                </div>
            </section>

            {/* Robot Snacks banner - only shows when an ask is active */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4">
                <RobotSnacksBanner />
            </section>

            {/* Global reach - Asia focus */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16" data-testid="global-reach">
                <div className="grid lg:grid-cols-12 gap-10 items-start">
                    <div className="lg:col-span-5">
                        <div className="eyebrow text-[var(--rl-primary)] mb-3 inline-flex items-center gap-2"><Globe className="w-3.5 h-3.5" /> Global reach · Asia focus</div>
                        <h2 className="font-serif text-3xl sm:text-5xl tracking-tight leading-[1.05]">The dogs the world<br />forgets first.</h2>
                        <p className="mt-4 text-[var(--rl-text-2)] max-w-md leading-relaxed">
                            Across Asia, an estimated 30 million street dogs and thousands of dog-meat-farm survivors live without a safety net. RescueLink connects them to adopters, fosters, and rescue partners worldwide — with transport, medical care, and legal escort already in place.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-2">
                            {["Soi Dog Foundation", "HSI Korea", "BAWA Bali", "Animals Asia", "PAWS Philippines", "Voice for Voiceless India"].map((r) => (
                                <span key={r} className="text-xs px-3 py-1 bg-[var(--rl-cream)] border border-[var(--rl-border)] rounded-full text-[var(--rl-text-2)]">{r}</span>
                            ))}
                        </div>
                        <Link to="/at-risk">
                            <Button className="btn-primary rounded-full h-11 px-5 mt-6" data-testid="global-cta">
                                See dogs in every region <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </Link>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="country-grid">
                            {regions.map((r) => (
                                <Link key={`${r.country}-${r.region}`} to={`/at-risk?country=${encodeURIComponent(r.country)}`}
                                      className="card-warm rounded-lg p-4 group" data-testid={`country-tile-${r.country.replace(/\s+/g, "-")}`}>
                                    <div className="eyebrow text-[var(--rl-text-3)] mb-1">{r.region}</div>
                                    <div className="font-serif text-xl leading-tight">{r.country}</div>
                                    <div className="mt-3 flex items-baseline justify-between">
                                        <span className="text-3xl font-serif text-[var(--rl-urgent)]">{r.count}</span>
                                        <span className="text-[10px] uppercase tracking-widest text-[var(--rl-text-3)]">at risk</span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* How the network works */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16" data-testid="how-it-works">
                <div className="eyebrow mb-3">How the network works</div>
                <h2 className="font-serif text-3xl sm:text-5xl tracking-tight max-w-2xl">One platform, every role in rescue.</h2>
                <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Feature
                        icon={<AlertTriangle className="w-5 h-5" />}
                        title="At-Risk Dashboard"
                        body="A live wall of dogs with countdowns to their euthanasia date. Pledge, foster, or coordinate transport in one click."
                        cta="Go to dashboard" to="/at-risk" testid="feature-atrisk"
                    />
                    <Feature
                        icon={<Search className="w-5 h-5" />}
                        title="Search everything"
                        body="Filter across dogs, rescues, shelters, and partner services by state, size, temperament, and more."
                        cta="Browse dogs" to="/dogs" testid="feature-search"
                    />
                    <Feature
                        icon={<Sparkles className="w-5 h-5" />}
                        title="AI Matchmaking"
                        body="Answer a short questionnaire — Claude Sonnet reviews the catalog and returns the five dogs most compatible with your life."
                        cta="Find my match" to="/matchmaking" testid="feature-ai"
                    />
                    <Feature
                        icon={<Users className="w-5 h-5" />}
                        title="Foster & Volunteer portals"
                        body="Signal availability, browse opportunities, and log hours from one dashboard."
                        cta="Join as volunteer" to="/volunteer" testid="feature-portal"
                    />
                    <Feature
                        icon={<Heart className="w-5 h-5" />}
                        title="Community feed"
                        body="Follow live success stories, urgent asks, and coordinate rescues in real time."
                        cta="Open feed" to="/community" testid="feature-community"
                    />
                    <Feature
                        icon={<Heart className="w-5 h-5" />} accent
                        title="Direct-to-rescue donations"
                        body={donations ? `${new Intl.NumberFormat("en-US", { style:"currency", currency:"USD", maximumFractionDigits:0 }).format(donations.total)} raised so far.` : "100% of your donation reaches rescue efforts."}
                        cta="Donate now" to="/donate" testid="feature-donate"
                    />
                </div>
            </section>

            {/* Community strip */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16" data-testid="community-strip">
                <div className="grid lg:grid-cols-2 gap-10 items-center bg-[var(--rl-cream)] rounded-2xl p-10 border border-[var(--rl-border)]">
                    <div>
                        <div className="eyebrow mb-3">Adoption stories</div>
                        <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">Every dog<br />becomes a story.</h2>
                        <p className="mt-4 text-[var(--rl-text-2)] max-w-md">Share and follow adoption journeys, rescue wins, and moments of joy from across the network.</p>
                        <Link to="/community">
                            <Button className="btn-primary rounded-full h-11 px-5 mt-6" data-testid="community-cta">Open community feed</Button>
                        </Link>
                    </div>
                    <div className="rounded-xl overflow-hidden border border-[var(--rl-border)]">
                        <img src="https://images.unsplash.com/photo-1526363269865-60998e11d82d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxoYXBweSUyMGZhbWlseSUyMHdpdGglMjBkb2clMjBhZG9wdGlvbnxlbnwwfHx8fDE3ODU0NjI5MDR8MA&ixlib=rb-4.1.0&q=85"
                             alt="Adoption success" className="w-full aspect-[4/3] object-cover" />
                    </div>
                </div>
            </section>
        </div>
    );
}

function Stat({ label, value, accent }) {
    return (
        <div>
            <div className={`font-serif text-4xl ${accent ? "text-[var(--rl-urgent)]" : "text-[var(--rl-text)]"}`}>{value}</div>
            <div className="eyebrow mt-1">{label}</div>
        </div>
    );
}

function Feature({ icon, title, body, cta, to, accent, testid }) {
    return (
        <div className="card-warm rounded-xl p-6" data-testid={testid}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-4 ${accent ? "bg-[var(--rl-primary)] text-white" : "bg-[var(--rl-cream)] text-[var(--rl-text)]"}`}>{icon}</div>
            <h3 className="font-serif text-2xl">{title}</h3>
            <p className="text-sm text-[var(--rl-text-2)] mt-2 leading-relaxed">{body}</p>
            <Link to={to} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--rl-primary)] mt-4 hover:underline">
                {cta} <ArrowRight className="w-4 h-4" />
            </Link>
        </div>
    );
}
